import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CLIENT_APPS,
  isAppVisibleToMerchants,
  readClientAppOverrides,
  type ClientApp,
  type ClientAppOverrides,
} from '@/lib/clientApps';

/**
 * Mini-applications Zifek visibles dans l'espace marchand.
 * Le SuperAdmin décide, application par application, si elle apparaît chez
 * les marchands, chez les utilisateurs normaux, ou les deux.
 */
export default function MerchantMiniApps() {
  const [overrides, setOverrides] = useState<ClientAppOverrides>({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    readClientAppOverrides()
      .then((data) => { if (alive) setOverrides(data); })
      .catch(() => undefined)
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, []);

  const apps: ClientApp[] = CLIENT_APPS.filter((app) => isAppVisibleToMerchants(app, overrides));

  if (!ready || apps.length === 0) return null;

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-bold font-heading text-foreground-950">Mini-applications Zifek</h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            Outils complémentaires mis à disposition de votre espace par la plateforme.
          </p>
        </div>
        <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-semibold whitespace-nowrap">
          {apps.length} disponible{apps.length > 1 ? 's' : ''}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {apps.map((app) => (
          <article
            key={app.slug}
            className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/60"
          >
            <span className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
              <i className={`${app.icon} text-lg`}></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground-900 truncate">{app.name}</p>
              <p className="text-[11px] text-foreground-500 truncate">{app.tagline}</p>
            </div>
            <Link
              to={app.route}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[11px] font-semibold whitespace-nowrap no-underline hover:bg-foreground-800 transition-colors"
            >
              <i className="ri-external-link-line"></i>
              Ouvrir
            </Link>
          </article>
        ))}
      </div>
    </section>
  );
}